import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Task } from '../types/task';
import type { RepoInfo } from '../types/environment';
import ArchiveIcon from './icons/ArchiveIcon';
import UnarchiveIcon from './icons/UnarchiveIcon';

interface WorkstreamTaskActionsProps {
  workstreamID: string;
  taskID: string;
  task: Task | null;
  onTaskChanged?: () => void;
}

function PlayIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      stroke="none"
    >
      <polygon points="6,3 20,12 6,21" />
    </svg>
  );
}

function CopyIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

function GitHubIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      aria-hidden="true"
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82a7.62 7.62 0 0 1 2-.27c.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.5.61.5 1.25 0 .9-.01 1.63-.01 1.85 0 .21.15.46.55.38A8.013 8.013 0 0 0 16 8c0-4.42-3.58-8-8-8Z" />
    </svg>
  );
}

function ExternalLinkIcon() {
  return (
    <svg
      aria-hidden="true"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M18 13v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

const btnStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '6px 16px',
  fontSize: '13px',
};

function isTaskExecutable(task: Task): boolean {
  if (task.Deleted) return false;
  if (!task.EnvironmentID) return false;
  if (!task.RepoInfo || Object.keys(task.RepoInfo).length === 0) return false;
  for (const repo of Object.values(task.RepoInfo)) {
    if (!repo.TargetBranch) return false;
  }
  return true;
}

function hasAnyTurn(task: Task): boolean {
  return (task.LastTurnIndex ?? 0) > 0 || !!task.TurnID;
}

function getPrEntries(task: Task): [string, string][] {
  if (!task.RepoInfo) return [];
  const entries: [string, string][] = [];
  for (const [repo, info] of Object.entries(task.RepoInfo)) {
    if (info.PRLink) {
      entries.push([repo, info.PRLink]);
    }
  }
  return entries.sort(([a], [b]) => a.localeCompare(b));
}

function cloneRepoInfo(repoInfo: Record<string, RepoInfo>): Record<string, RepoInfo> {
  const clone: Record<string, RepoInfo> = {};
  for (const [key, val] of Object.entries(repoInfo)) {
    clone[key] = {
      FeatureBranch: val.FeatureBranch,
      TargetBranch: val.TargetBranch,
    };
  }
  return clone;
}

export default function WorkstreamTaskActions({ workstreamID, taskID, task, onTaskChanged }: WorkstreamTaskActionsProps) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [prMenuOpen, setPrMenuOpen] = useState(false);
  const prMenuRef = useRef<HTMLDivElement>(null);

  const wsQuery = `?workstreamID=${encodeURIComponent(workstreamID)}`;

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (prMenuRef.current && !prMenuRef.current.contains(e.target as Node)) {
        setPrMenuOpen(false);
      }
    }
    if (prMenuOpen) {
      document.addEventListener('click', handleClickOutside);
    }
    return () => document.removeEventListener('click', handleClickOutside);
  }, [prMenuOpen]);

  const prEntries = useMemo(() => (task ? getPrEntries(task) : []), [task]);
  const hasPRs = prEntries.length > 0;
  const hasMultiplePRs = prEntries.length > 1;

  const canExecute = task ? isTaskExecutable(task) && !hasAnyTurn(task) : false;
  const showExecute = canExecute;
  const showViewPR = hasPRs;

  const handleExecute = useCallback(async () => {
    if (!task || busy) return;
    setBusy(true);
    setError(null);
    try {
      const resp = await fetch(`/api/tasks/${encodeURIComponent(taskID)}/turns/1${wsQuery}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'If-Match': String(task.Version ?? 0),
        },
        body: JSON.stringify({ Prompt: task.Prompt ?? '' }),
      });
      if (!resp.ok) {
        const text = await resp.text();
        setError(text || 'Failed to execute task');
      } else {
        onTaskChanged?.();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to execute task');
    } finally {
      setBusy(false);
    }
  }, [task, taskID, wsQuery, busy, onTaskChanged]);

  const handleArchiveToggle = useCallback(async () => {
    if (!task || busy) return;
    setBusy(true);
    setError(null);
    const isDeleted = task.Deleted;
    try {
      const url = isDeleted
        ? `/api/workstreams/${encodeURIComponent(workstreamID)}/tasks/${encodeURIComponent(taskID)}/undelete`
        : `/api/workstreams/${encodeURIComponent(workstreamID)}/tasks/${encodeURIComponent(taskID)}`;
      const method = isDeleted ? 'POST' : 'DELETE';
      const resp = await fetch(url, { method });
      if (!resp.ok && resp.status !== 204) {
        const text = await resp.text();
        setError(text || `Failed to ${isDeleted ? 'unarchive' : 'archive'} task`);
      } else {
        onTaskChanged?.();
      }
    } catch (err: any) {
      setError(err.message || 'Operation failed');
    } finally {
      setBusy(false);
    }
  }, [task, taskID, workstreamID, busy, onTaskChanged]);

  const handleClone = useCallback(async () => {
    if (!task || busy) return;
    setBusy(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        Title: `${task.Title} - Copy`,
        Parallel: false,
      };
      if (task.EnvironmentID) {
        body.EnvironmentID = task.EnvironmentID;
      }
      if (task.Prompt) {
        body.Prompt = task.Prompt;
      }
      if (task.RepoInfo && Object.keys(task.RepoInfo).length > 0) {
        body.RepoInfo = cloneRepoInfo(task.RepoInfo);
      }

      const resp = await fetch(`/api/workstreams/${encodeURIComponent(workstreamID)}/tasks`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        const text = await resp.text();
        setError(text || 'Failed to clone task');
      } else {
        const newTask: Task = await resp.json();
        navigate(`/workstreams/tasks/${encodeURIComponent(workstreamID)}/${encodeURIComponent(newTask.TaskID)}`);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to clone task');
    } finally {
      setBusy(false);
    }
  }, [task, workstreamID, busy, navigate]);

  if (!task) return null;

  const isDeleted = task.Deleted;

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          flexWrap: 'wrap',
        }}
      >
        {/* Execute Task — only shown when executable */}
        {showExecute && (
          <button
            type="button"
            disabled={busy}
            onClick={handleExecute}
            style={{
              ...btnStyle,
              '--button-bg': '#28a745',
              '--button-bg-hover': '#22863a',
              '--button-bg-active': '#1e7e34',
            } as React.CSSProperties}
          >
            <PlayIcon />
            Execute Task
          </button>
        )}

        {/* Archive / Unarchive */}
        <button
          type="button"
          className={isDeleted ? undefined : 'btn-danger'}
          disabled={busy}
          onClick={handleArchiveToggle}
          style={btnStyle}
        >
          {isDeleted ? <UnarchiveIcon size={16} /> : <ArchiveIcon size={16} />}
          {isDeleted ? 'Unarchive Task' : 'Archive Task'}
        </button>

        {/* View PR / View PRs — only shown when PRs exist */}
        {showViewPR && (
          hasMultiplePRs ? (
            <div ref={prMenuRef} style={{ position: 'relative', display: 'inline-flex' }}>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setPrMenuOpen((o) => !o)}
                style={btnStyle}
              >
                <GitHubIcon />
                View PRs
                <svg
                  aria-hidden="true"
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M4.646 6.646a.5.5 0 0 1 .708 0L8 9.293l2.646-2.647a.5.5 0 0 1 .708.708l-3 3a.5.5 0 0 1-.708 0l-3-3a.5.5 0 0 1 0-.708z" />
                </svg>
              </button>
              {prMenuOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 4px)',
                    left: 0,
                    background: '#fff',
                    border: '1px solid #ccc',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    minWidth: '220px',
                    overflow: 'hidden',
                    zIndex: 1000,
                  }}
                >
                  {prEntries.map(([repo, link], index) => (
                    <a
                      key={repo}
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '10px 14px',
                        textDecoration: 'none',
                        color: '#1f2933',
                        fontSize: '14px',
                        background: '#fff',
                        borderTop: index === 0 ? 'none' : '1px solid #eee',
                      }}
                    >
                      <span
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={repo}
                      >
                        {repo}
                      </span>
                      <ExternalLinkIcon />
                    </a>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                if (prEntries.length === 1) {
                  window.open(prEntries[0][1], '_blank', 'noopener,noreferrer');
                }
              }}
              style={btnStyle}
            >
              <GitHubIcon />
              View PR
            </button>
          )
        )}

        {/* Clone Task */}
        <button
          type="button"
          className="btn-blue"
          disabled={busy}
          onClick={handleClone}
          style={btnStyle}
        >
          <CopyIcon />
          Clone Task
        </button>
      </div>

      {error && (
        <div
          className="error-banner"
          style={{
            margin: '8px 0 0',
            padding: '8px 12px',
            background: '#ffebe9',
            border: '1px solid #cf222e',
            borderRadius: '6px',
            color: '#cf222e',
            fontSize: '13px',
          }}
        >
          {error}
          <button
            type="button"
            className="error-banner__close"
            onClick={() => setError(null)}
          >
            &times;
          </button>
        </div>
      )}
    </div>
  );
}
